import { useState } from 'react';
import { Button } from '../components/Button.js';
import { Input } from '../components/Input.js';
import { api, type ApiError } from '../lib/api.js';
import { useAuthStore } from '../lib/auth-store.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';

interface StatusResponse {
  hasTotp: boolean;
  isAllowedEmail: boolean;
}

interface SetupResponse {
  setupId: string;
  qrDataUrl: string;
  secret: string;
}

interface AuthResponse {
  ok: boolean;
  deviceId: string;
  userId: string;
}

type Step =
  | 'email' // Ввод email
  | 'totp-login' // Ввод TOTP кода (уже настроен)
  | 'setup-code' // Ввод setup-кода из логов
  | 'setup-qr' // Отображение QR + ввод TOTP кода
  | 'setup-code-sent'; // Код отправлен в логи, ожидаем ввода

export function AuthScreen() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [setupId, setSetupId] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setAuth = useAuthStore((s) => s.setAuth);
  useDocumentTitle(buildTitle('Вход'));

  function reset() {
    setStep('email');
    setTotpCode('');
    setSetupCode('');
    setSetupId('');
    setQrDataUrl('');
    setSecret('');
    setError(null);
  }

  // Шаг 1: проверяем статус email
  async function checkEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<StatusResponse>('/api/v1/auth/status', {
        email: email.trim(),
      });
      if (!res.isAllowedEmail) {
        setError('email_not_allowed');
      } else if (res.hasTotp) {
        setStep('totp-login');
      } else {
        // TOTP не настроен — начинаем setup
        await requestSetup();
      }
    } catch (err) {
      setError((err as ApiError).code ?? 'network_error');
    } finally {
      setLoading(false);
    }
  }

  // Запросить setup-код (будет в логах)
  async function requestSetup() {
    setLoading(true);
    try {
      await api.post('/api/v1/auth/totp/request-setup', { email: email.trim() });
      setStep('setup-code-sent');
    } catch (err) {
      setError((err as ApiError).code ?? 'network_error');
    } finally {
      setLoading(false);
    }
  }

  // Подтвердить setup-код → получить QR
  async function verifySetupCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<SetupResponse>('/api/v1/auth/totp/setup', {
        email: email.trim(),
        setupCode: setupCode.trim(),
      });
      setSetupId(res.setupId);
      setQrDataUrl(res.qrDataUrl);
      setSecret(res.secret);
      setStep('setup-qr');
    } catch (err) {
      setError((err as ApiError).code ?? 'network_error');
    } finally {
      setLoading(false);
    }
  }

  // Активировать TOTP (финал setup)
  async function activateTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<AuthResponse>('/api/v1/auth/totp/activate', {
        email: email.trim(),
        setupId,
        totpCode: totpCode.trim(),
        deviceName: defaultDeviceName(),
        userAgent: navigator.userAgent,
      });
      setAuth(res.deviceId, res.userId);
    } catch (err) {
      setError((err as ApiError).code ?? 'network_error');
    } finally {
      setLoading(false);
    }
  }

  // Логин по TOTP
  async function loginWithTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.post<AuthResponse>('/api/v1/auth/totp/login', {
        email: email.trim(),
        totpCode: totpCode.trim(),
        deviceName: defaultDeviceName(),
        userAgent: navigator.userAgent,
      });
      setAuth(res.deviceId, res.userId);
    } catch (err) {
      setError((err as ApiError).code ?? 'network_error');
    } finally {
      setLoading(false);
    }
  }

  function errorText(code: string): string {
    switch (code) {
      case 'email_not_allowed':
        return 'Email не разрешён';
      case 'invalid_code':
        return 'Неверный код';
      case 'invalid_setup':
        return 'Сессия настройки истекла';
      case 'invalid_totp':
        return 'Неверный TOTP код';
      case 'totp_already_configured':
        return 'TOTP уже настроен';
      case 'totp_not_configured':
        return 'TOTP не настроен';
      case 'too_many_attempts':
        return 'Слишком много попыток';
      case 'invalid_credentials':
        return 'Неверные данные';
      default:
        return `Ошибка: ${code}`;
    }
  }

  return (
    <main className="safe-top min-h-dvh flex flex-col items-center justify-center p-6 gap-6">
      <div className="w-full max-w-sm space-y-4">
        <header className="text-center space-y-2">
          <h1 className="text-3xl font-semibold">Claude Remote</h1>
          <StepDescription step={step} email={email} />
        </header>

        {step === 'email' && (
          <form onSubmit={checkEmail} className="space-y-3">
            <Input
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full"
              required
              autoFocus
            />
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading || !email}
            >
              {loading ? 'Проверяем…' : 'Продолжить'}
            </Button>
          </form>
        )}

        {step === 'totp-login' && (
          <form onSubmit={loginWithTotp} className="space-y-3">
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className="w-full text-center tracking-widest text-2xl font-mono"
              autoFocus
            />
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading || totpCode.length !== 6}
            >
              {loading ? 'Входим…' : 'Войти'}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={reset}>
              Сменить email
            </Button>
          </form>
        )}

        {step === 'setup-code-sent' && (
          <form onSubmit={verifySetupCode} className="space-y-3">
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
              className="w-full text-center tracking-widest text-2xl font-mono"
              autoFocus
            />
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading || setupCode.length !== 6}
            >
              {loading ? 'Проверяем…' : 'Подтвердить'}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={reset}>
              Отмена
            </Button>
          </form>
        )}

        {step === 'setup-qr' && (
          <form onSubmit={activateTotp} className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              <img
                src={qrDataUrl}
                alt="QR код для Google Authenticator"
                className="w-48 h-48 bg-white rounded-lg p-2"
              />
              <p className="text-xs text-neutral-400 text-center">
                Или введи вручную:
                <br />
                <code className="text-neutral-300 select-all break-all">{secret}</code>
              </p>
            </div>
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="Код из приложения"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className="w-full text-center tracking-widest text-2xl font-mono"
              autoFocus
            />
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading || totpCode.length !== 6}
            >
              {loading ? 'Активируем…' : 'Активировать TOTP'}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={reset}>
              Отмена
            </Button>
          </form>
        )}

        {error && <p className="text-sm text-red-400 text-center">{errorText(error)}</p>}
      </div>
    </main>
  );
}

function StepDescription({ step, email }: { step: Step; email: string }) {
  switch (step) {
    case 'email':
      return <p className="text-neutral-400 text-sm">Введи email для входа</p>;
    case 'totp-login':
      return (
        <p className="text-neutral-400 text-sm">
          Введи 6-значный код из Google Authenticator
          <br />
          <span className="text-neutral-500">{email}</span>
        </p>
      );
    case 'setup-code-sent':
      return (
        <p className="text-neutral-400 text-sm">
          Код для настройки TOTP отправлен в логи сервера.
          <br />
          <code className="text-neutral-500 text-xs">journalctl -u claude-remote -n 50</code>
        </p>
      );
    case 'setup-qr':
      return (
        <p className="text-neutral-400 text-sm">
          Отсканируй QR в Google Authenticator,
          <br />
          затем введи 6-значный код
        </p>
      );
    default:
      return null;
  }
}

function defaultDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android';
  return 'Web';
}
